import { MinerControlAction3 } from "@mobx/api/miner";
import { MinerHashEvent } from "@services/http/socket/responses";
import { EQUIPMENT_TYPE } from "@store/Equipments/actionTypes";
import { Point } from "framer-motion";
import { action, makeObservable, observable } from "mobx";
import { Connection, Edge, EdgeChange, NodeChange, Viewport, XYPosition } from "reactflow";
import { TypePartialRackItem3 } from "../locations/types";
import {
  EditorOps,
  MinersChangesTable,
  PosChanges,
  RacksChangesTable,
  TurnOnInfo,
  createDataOps,
  createHistory,
} from "./ops";
import {
  CreateConsumersActionResult3,
  DraggedElementData3,
  EditorData3,
  NodeSignal3,
  NodeType3,
  PlaceID3,
} from "./types";

export interface EditorState3 {
  locked: boolean;
  loading: boolean;
  data: EditorData3;
  undoLength: number;
  redoLength: number;
  toTurnOn?: TurnOnInfo;
  viewport?: Viewport;
}

const createInitialEditorData = (): EditorData3 => ({
  lastNodeID: 0,
  nodesCounter: {
    cell: 0,
    transformer: 0,
    counter: 0,
    shield: 0,
    consumer: 0,
    network: 0,
    circle: 0,
    triangle: 0,
    square: 0,
    diamond: 0,
  },
  nodes: [],
  edges: [],
  tree: {
    forth: {},
  },
});

export const createInitialEditorState = (): EditorState3 => ({
  loading: false,
  locked: false,
  undoLength: 0,
  redoLength: 0,
  data: createInitialEditorData(),
  toTurnOn: undefined,
  viewport: undefined,
});

export interface EditorStore3 extends EditorState3 {
  placeID: PlaceID3;
  kind: EQUIPMENT_TYPE;
  setLoading: (on: boolean) => void;
  connect: (edge: Edge<any> | Connection) => boolean | undefined;
  applyNodeChanges: (changes: NodeChange[]) => boolean | undefined;
  applyEdgeChanges: (changes: EdgeChange[]) => boolean | undefined;
  sendSignal: (nodeID: string, signal: NodeSignal3) => boolean | undefined;
  applyUndoRedo: (undo: boolean) => boolean | undefined;
  clear: () => boolean | undefined;
  newNode: (type: NodeType3, position: XYPosition) => boolean | undefined;
  removeNode: (nodeID: number | string) => boolean | undefined;
  removeEdge: (nodeID: number) => boolean | undefined;
  setNodeName: (nodeID: number, name: string) => boolean | undefined;
  editNodeInfo: (
    nodeID: string,
    name: string,
    notes: string,
    color: string
  ) => boolean | undefined;
  createConsumers: (items: DraggedElementData3[], point: Point) => CreateConsumersActionResult3;
  mergeRacks: (rtab: RacksChangesTable) => boolean | undefined;
  removeRacks: (rackIDs: Set<number>) => boolean | undefined;
  changeRackItems: (rackID: number, items: TypePartialRackItem3[], miners: MinersChangesTable) => boolean | undefined;
  changeMinerState: (ev: MinerHashEvent) => boolean | undefined;
  reportMinersControl: (control: MinerControlAction3, nodes: Set<string>, success: boolean) => boolean | undefined;
  setEditorLock: (lock: boolean) => boolean | undefined;
  saveEditorViewport: (v: Viewport) => boolean | undefined;
  replaceData: (data: EditorData3) => void;
  resetToInitialState: () => void;
}

export const createEditorStore = (params: {
  placeID: PlaceID3;
  kind: EQUIPMENT_TYPE;
  nextColorIndex: () => number;
}): EditorStore3 => {
  const { placeID, kind, nextColorIndex } = params;
  const history = createHistory();
  const posChanges: PosChanges = { changes: {} };

  const self: EditorStore3 = {
    placeID,
    kind,
    ...createInitialEditorState(),

    setLoading(on: boolean) {
      self.loading = on;
    },

    connect(edge: Edge<any> | Connection) {
      const ops = createOps();
      const data = ops.addEdge(edge);
      applyOpsResult(data, ops);
      return false;
    },

    applyNodeChanges(changes: NodeChange[]) {
      const ops = createOps();
      const data = ops.changeNodes(changes);
      applyOpsResult(data, ops);
      if (changes.every((change) => change.type === "dimensions")) return undefined;
      return false;
    },

    applyEdgeChanges(changes: EdgeChange[]) {
      const ops = createOps();
      const data = ops.changeEdges(changes);
      applyOpsResult(data, ops);
      return false;
    },

    sendSignal(nodeID: string, signal: NodeSignal3) {
      const ops = createOps();
      const data = ops.sendSignal(nodeID, signal);
      applyOpsResult(data, ops);
      return true;
    },

    applyUndoRedo(undo: boolean) {
      const ops = createOps();
      const data = undo ? ops.undo() : ops.redo();
      applyOpsResult(data, ops);
      return false;
    },

    clear() {
      const ops = createOps();
      const empty = createInitialEditorData();
      const data = ops.clear(empty);
      self.data = data;
      self.undoLength = ops.undoLength;
      self.redoLength = ops.redoLength;
      return false;
    },

    newNode(type: NodeType3, position: XYPosition) {
      const ops = createOps();
      const data = ops.addNode2(type, position);
      applyOpsResult(data, ops);
      return false;
    },

    removeNode(nodeID: number | string) {
      const ops = createOps();
      const data = ops.removeNode(nodeID);
      applyOpsResult(data, ops);
      return false;
    },

    removeEdge(nodeID: number) {
      const ops = createOps();
      const data = ops.removeEdge(nodeID);
      applyOpsResult(data, ops);
      return false;
    },

    setNodeName(nodeID: number, name: string) {
      const ops = createOps();
      const data = ops.setNodeName(nodeID, name);
      applyOpsResult(data, ops);
      return false;
    },

    editNodeInfo(nodeID: string, name: string, notes: string, color: string) {
      const ops = createOps();
      const data = ops.editNodeInfo(nodeID, name, notes, color);
      applyOpsResult(data, ops);
      return false;
    },

    createConsumers(items: DraggedElementData3[], point: Point) {
      const ops = createOps();
      const result = ops.createConsumers(items, point);
      applyOpsResult(result.data, ops);
      return {
        savedHint: result.meta.createdNodeIDs.length ? false : undefined,
        meta: result.meta,
      };
    },

    mergeRacks(rtab: RacksChangesTable) {
      const ops = createOps();
      const result = ops.mergeRacks(rtab);
      if (!result.changed) return undefined;
      applyOpsResult(result.data, ops);
      return false;
    },

    removeRacks(rackIDs: Set<number>) {
      const ops = createOps();
      const result = ops.removeRacks(rackIDs);
      if (!result.changed) return undefined;
      applyOpsResult(result.data, ops);
      return false;
    },

    changeRackItems(rackID: number, items: TypePartialRackItem3[], miners: MinersChangesTable) {
      const ops = createOps();
      const result = ops.changeRackItems(rackID, items, miners);
      self.toTurnOn = result.toTurnOn;
      if (!result.changed) return undefined;
      applyOpsResult(result.data, ops);
      return false;
    },

    changeMinerState(ev: MinerHashEvent) {
      const ops = createOps();
      const data = ops.changeMinerState(ev);
      applyOpsResult(data, ops);
      return true;
    },

    reportMinersControl(control: MinerControlAction3, nodes: Set<string>, success: boolean) {
      const ops = createOps();
      const data = ops.reportMinersControl(control, nodes, success);
      applyOpsResult(data, ops);
      self.toTurnOn = undefined;
      return success ? false : undefined;
    },

    setEditorLock(lock: boolean) {
      self.locked = lock;
      return undefined;
    },

    saveEditorViewport(v: Viewport) {
      self.viewport = v;
      return undefined;
    },

    replaceData(data: EditorData3) {
      self.data = data;
    },

    resetToInitialState() {
      self.loading = false;
      self.locked = false;
      self.undoLength = 0;
      self.redoLength = 0;
      self.data = createInitialEditorData();
      self.toTurnOn = undefined;
      self.viewport = undefined;
    },
  };

  const createOps = (): EditorOps =>
    createDataOps({
      placeID,
      data: self.data,
      kind,
      history,
      posChanges,
      nextColorIndex,
    });

  const applyOpsResult = (data: EditorData3, ops: EditorOps) => {
    self.data = data;
    self.undoLength = ops.undoLength;
    self.redoLength = ops.redoLength;
  };

  return makeObservable(self, {
    loading: observable,
    locked: observable,
    data: observable,
    undoLength: observable,
    redoLength: observable,
    toTurnOn: observable,
    viewport: observable,
    setLoading: action,
    connect: action,
    applyNodeChanges: action,
    applyEdgeChanges: action,
    sendSignal: action,
    applyUndoRedo: action,
    clear: action,
    newNode: action,
    removeNode: action,
    removeEdge: action,
    setNodeName: action,
    editNodeInfo: action,
    createConsumers: action,
    mergeRacks: action,
    removeRacks: action,
    changeRackItems: action,
    changeMinerState: action,
    reportMinersControl: action,
    setEditorLock: action,
    saveEditorViewport: action,
    replaceData: action,
    resetToInitialState: action,
  });
};
